/**
 * Email Service - Handles email verification and confirmation
 * 
 * @description
 * Service for managing email verification, resending confirmation emails,
 * and handling forgot password email functionality.
 * 
 * @features
 * - Send verification emails after signup
 * - Verify email tokens from confirmation links
 * - Resend verification emails
 * - Handle forgot password email flow
 * - Track verification status
 */

import { makeRequest } from './api.js';

/**
 * Send verification email to user
 * @param {string} email - User's email address
 * @returns {Promise} API response
 */
export const sendVerificationEmail = async (email) => {
  try {
    const response = await makeRequest('/auth/send-verification', {
      method: 'POST',
      body: { email }
    });
    
    if (import.meta.env.DEV) {
      console.log('✅ Verification email sent successfully');
    }
    
    return response;
  } catch (error) {
    console.error('Failed to send verification email:', error);
    throw error;
  }
};

/**
 * Verify email with token from confirmation link
 * @param {string} token - Verification token from email link
 * @returns {Promise} API response
 */
export const verifyEmail = async (token) => {
  try {
    const response = await makeRequest('/auth/verify-email', {
      method: 'POST',
      body: { token }
    });
    
    if (import.meta.env.DEV) {
      console.log('✅ Email verified successfully');
    }
    
    return response;
  } catch (error) {
    console.error('Failed to verify email:', error);
    throw error;
  }
};

/**
 * Resend verification email for current user (with fallback)
 * @returns {Promise} API response
 */
export const resendVerificationEmail = async () => {
  try {
    // First try with JWT authentication
    const response = await makeRequest('/auth/resend-verification', {
      method: 'POST'
    });
    
    if (import.meta.env.DEV) {
      console.log('✅ Verification email resent successfully');
    }
    
    return response;
  } catch (error) {
    // If JWT fails (401), try to get user email and use fallback
    if (error.message.includes('401')) {
      console.warn('JWT expired, attempting fallback email resend...');
      // You might want to get the email from user input or localStorage
      throw new Error('Please provide your email address to resend verification');
    }
    
    console.error('Failed to resend verification email:', error);
    throw error;
  }
};

/**
 * Resend verification email by email address (no authentication required)
 * @param {string} email - User's email address
 * @returns {Promise} API response
 */
export const resendVerificationByEmail = async (email) => {
  try {
    const response = await makeRequest('/auth/resend-verification-by-email', {
      method: 'POST',
      body: { email }
    });
    
    if (import.meta.env.DEV) {
      console.log('✅ Verification email resent successfully by email');
    }
    
    return response;
  } catch (error) {
    console.error('Failed to resend verification email by email:', error);
    throw error;
  }
};

/**
 * Check email verification status for current user
 * @returns {Promise} API response with verification status
 */
export const checkVerificationStatus = async () => {
  try {
    const response = await makeRequest('/auth/verification-status');
    
    if (import.meta.env.DEV) {
      console.log('Email verification status:', response.verified);
    }
    
    return response;
  } catch (error) {
    console.error('Failed to check verification status:', error);
    throw error;
  }
};

/**
 * Send password reset email
 * @param {string} email - User's email address
 * @returns {Promise} API response
 */
export const sendPasswordResetEmail = async (email) => {
  try {
    const response = await makeRequest('/auth/forgot-password', {
      method: 'POST',
      body: { email }
    });
    
    if (import.meta.env.DEV) {
      console.log('✅ Password reset email sent successfully');
    }
    
    return response;
  } catch (error) {
    console.error('Failed to send password reset email:', error);
    throw error;
  }
};

/**
 * Reset password with token from email link
 * @param {string} token - Reset token from email link
 * @param {string} newPassword - New password
 * @returns {Promise} API response
 */
export const resetPassword = async (token, newPassword) => {
  try {
    const response = await makeRequest('/auth/reset-password-with-token', {
      method: 'POST',
      body: { token, password: newPassword }
    });
    
    if (import.meta.env.DEV) {
      console.log('✅ Password reset successfully');
    }
    
    return response;
  } catch (error) {
    console.error('Failed to reset password:', error);
    throw error;
  }
};

/**
 * Change password when user knows current password (authenticated)
 * @param {string} currentPassword - Current password
 * @param {string} newPassword - New password
 * @returns {Promise} API response
 */
export const changePassword = async (currentPassword, newPassword) => {
  try {
    const response = await makeRequest('/auth/reset-password', {
      method: 'POST',
      body: { 
        current_password: currentPassword,
        new_password: newPassword 
      }
    });
    
    if (import.meta.env.DEV) {
      console.log('✅ Password changed successfully');
    }
    
    return response;
  } catch (error) {
    console.error('Failed to change password:', error);
    throw error;
  }
};
